import { useEffect, useState } from "react";
import Label from "../../../../components/form_fields/Label";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import { useForm } from "react-hook-form";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { AiOutlineDownCircle } from "react-icons/ai";
// import "react-phone-input-2/lib/style.css";
import {
	PermissionChecker,
	PERMISSIONS,
} from "../../../../helpers/usePermissionManager";
import useUserStore from "../../../../state-management/useUserStore";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import {
	useGetCompanyApprover,
	useCreateCompanyApprover,
	useUpdateCompanyApprover,
} from "../../../../services/company-approver.service";
import {
	CompanyApproverDto,
	CreateCompanyApproverDto,
	UpdateCompanyApproverDto,
} from "../../../../models/company-approver.dto";
import { useGetCompanyLevel } from "../../../../services/company-level.service";
import { CompanyLevelOrderBy } from "../../../../models/company-level.dto";
import { APP_CONFIG } from "../../../../helpers/appHelpers";

// ─── Approver Level Accordion ─────────────────────────────────────────────────

const ApproverLevelAccordion = ({
	levelNumber,
	approver,
	requestType,
	companyLevels,
	companyId,
	onSaveSuccess,
}: {
	levelNumber: number;
	approver?: CompanyApproverDto;
	requestType: "LEAVE" | "REIMBURSEMENT" | "WORK_TIME" | "OVERTIME";
	companyLevels: any[];
	companyId: number;
	onSaveSuccess?: () => void;
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [isEditing, setIsEditing] = useState(false);

	const createMutation = useCreateCompanyApprover();
	const updateMutation = useUpdateCompanyApprover();

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
		watch,
	} = useForm({
		defaultValues: {
			approverType: approver?.approverType || "",
			companyLevel: approver?.companyLevelId?.toString() || "",
		},
	});

	const approverType = watch("approverType");

	useEffect(() => {
		if (approver) {
			setValue("approverType", approver.approverType);
			setValue("companyLevel", approver.companyLevelId?.toString() || "");
		}
	}, [approver, setValue]);

	const onSubmit = async (data: {
		approverType: string;
		companyLevel: string;
	}) => {
		try {
			const level = Math.floor(Number(levelNumber));
			if (!Number.isFinite(level) || level < 1) return;

			const companyLevelId =
				data.approverType === "MANAGER" && data.companyLevel
					? Math.floor(Number(data.companyLevel))
					: undefined;

			if (approver?.id) {
				const payload: UpdateCompanyApproverDto = {
					approverType: data.approverType as any,
					requestType: requestType as any,
					...(companyLevelId !== undefined && { companyLevelId }),
				};
				await updateMutation.mutateAsync({
					payload,
					id: approver.id,
					companyId,
				});
			} else {
				const payload: CreateCompanyApproverDto = {
					approverType: data.approverType as any,
					level,
					requestType: requestType as any,
					...(companyLevelId !== undefined && { companyLevelId }),
				};
				await createMutation.mutateAsync({ payload, companyId });
			}

			setIsEditing(false);
			onSaveSuccess?.();
		} catch (error) {
			console.error(`Error saving ${requestType} approver:`, error);
		}
	};

	const handleCancel = () => {
		setIsEditing(false);
		if (approver) {
			setValue("approverType", approver.approverType);
			setValue("companyLevel", approver.companyLevelId?.toString() || "");
		} else {
			reset();
		}
	};

	const isLoading = createMutation.isLoading || updateMutation.isLoading;

	return (
		<Accordion
			expanded={isExpanded}
			onChange={() => setIsExpanded(!isExpanded)}
			classes={{ root: "border border-gray-300 rounded-lg py-2 mb-2" }}
		>
			<AccordionSummary
				expandIcon={<AiOutlineDownCircle className="text-xl" />}
				aria-controls={`${requestType}-level${levelNumber}-content`}
				id={`${requestType}-level${levelNumber}-header`}
				sx={{ px: 3, border: "1px" }}
			>
				<h2 className="font-inter text-sm font-semibold">
					Level {levelNumber}
				</h2>
			</AccordionSummary>
			<AccordionDetails sx={{ px: 3 }}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="border-b-[1.5px] pb-3 flex justify-between font-semibold text-2xs">
						<h2 className="font-semibold text-2xs">Details</h2>
						<PermissionChecker
							permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
						>
							<button
								type="button"
								className={`border-none text-primary-blue text-2xs ${isEditing ? "hidden" : ""}`}
								onClick={() => setIsEditing(true)}
							>
								Edit
							</button>
						</PermissionChecker>
					</div>

					<div className="mt-4">
						<Label
							title="Approver Type"
							for={`${requestType}ApproverType${levelNumber}`}
						/>
						<select
							className={`w-full bg-secondary-gray rounded-lg h-12 px-5 mt-2 text-sm border-none ${
								!isEditing ? "opacity-60" : ""
							}`}
							disabled={!isEditing}
							{...register("approverType", {
								required: "Approver type is required",
							})}
						>
							<option value="">Select Option</option>
							<option value="HR">HR</option>
							<option value="MANAGER">Manager</option>
							<option value="SUPERVISOR">Supervisor</option>
							<option value="DEPARMENT_HEAD">Department Head</option>
						</select>
						{errors.approverType && (
							<p className="text-red-500 text-xs mt-1">
								{errors.approverType.message as string}
							</p>
						)}

						{approverType === "MANAGER" && (
							<div className="mt-4">
								<Label
									title="Manager Level"
									for={`${requestType}CompanyLevel${levelNumber}`}
								/>
								<select
									className={`w-full bg-secondary-gray rounded-lg h-12 px-5 mt-2 text-sm border-none ${
										!isEditing ? "opacity-60" : ""
									}`}
									disabled={!isEditing}
									{...register("companyLevel")}
								>
									<option value="">Select Company Level</option>
									{companyLevels.map((companyLevel) => (
										<option key={companyLevel.id} value={companyLevel.id}>
											{companyLevel.levelName}
										</option>
									))}
								</select>
							</div>
						)}
					</div>

					{isEditing && (
						<div className="flex w-full justify-end space-x-4 items-center mt-10">
							<button
								type="button"
								className="px-12 py-4 border text-gray-400 hover:text-gray-500 border-gray-300 rounded-lg text-2xs"
								onClick={handleCancel}
								disabled={isLoading}
							>
								Cancel
							</button>
							<OrangeButton
								title={isLoading ? "Saving..." : "Save"}
								className="px-12"
								type="submit"
								disabled={isLoading}
							/>
						</div>
					)}
				</form>
			</AccordionDetails>
		</Accordion>
	);
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CompanyApprover = () => {
	const companyProfile = (useUserStore().currentUser.user.companyProfiles ||
		[])[0];
	const companyId = companyProfile?.companyId;
	const currentCompany = useCompanyStore().currentCompany;

	const { data: allCompanyLevels } = useGetCompanyLevel({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: CompanyLevelOrderBy.LEVEL_NAME_ASC,
	});
	const companyLevels = allCompanyLevels?.data || [];

	const { data: approversData, isLoading: approversLoading } =
		useGetCompanyApprover(companyId, {
			page: 1,
			limit: 100,
			orderBy: "createdAt:asc" as any,
		});
	const allApprovers = approversData?.data || [];

	// Filter by requestType so sections don't bleed into each other.
	// Rows with null requestType fall back to LEAVE for backward compatibility.
	const leaveApprovers = allApprovers.filter(
		(a) => a.requestType === "LEAVE" || !a.requestType,
	);
	const reimbursementApprovers = allApprovers.filter(
		(a) => a.requestType === "REIMBURSEMENT",
	);
	const workTimeApprovers = allApprovers.filter(
		(a) => a.requestType === "WORK_TIME",
	);
	const overtimeApprovers = allApprovers.filter(
		(a) => a.requestType === "OVERTIME",
	);

	const leaveApprovalsRequired =
		currentCompany?.leaveRequestApprovalsRequired || 0;
	const reimbursementApprovalsRequired =
		currentCompany?.reimbursementRequestApprovalsRequired || 0;
	const workTimeApprovalsRequired =
		currentCompany?.employeeWorkTimeRequestApprovalsRequired || 0;
	const overtimeApprovalsRequired =
		currentCompany?.employeeOvertimeEntryRequestApprovalsRequired || 0;

	if (approversLoading) return <div>Loading...</div>;

	return (
		<div>
			<div className="border-b-[1.5px] pb-3 flex justify-between font-semibold text-sm">
				<h2 className="font-semibold text-sm">Company Approvers</h2>
			</div>

			{/* ── Leave ── */}
			<h2 className="font-semibold text-sm py-3">Leave Request Approvals</h2>
			{leaveApprovalsRequired === 0 ? (
				<p className="text-gray-500 text-sm mb-4">
					No leave approval levels configured. Please set the number of required
					approvals in Company Details.
				</p>
			) : (
				Array.from({ length: leaveApprovalsRequired }, (_, i) => i + 1).map(
					(level) => (
						<ApproverLevelAccordion
							key={`leave-${level}`}
							levelNumber={level}
							approver={leaveApprovers.find((a) => a.level === level)}
							requestType="LEAVE"
							companyLevels={companyLevels}
							companyId={companyId}
						/>
					),
				)
			)}

			{/* ── Reimbursement ── */}
			<h2 className="font-semibold text-sm py-3">Reimbursement Approvals</h2>
			{reimbursementApprovalsRequired === 0 ? (
				<p className="text-gray-500 text-sm mb-4">
					No reimbursement approval levels configured. Please set the number of
					required approvals in Company Details.
				</p>
			) : (
				Array.from(
					{ length: reimbursementApprovalsRequired },
					(_, i) => i + 1,
				).map((level) => (
					<ApproverLevelAccordion
						key={`reimbursement-${level}`}
						levelNumber={level}
						approver={reimbursementApprovers.find((a) => a.level === level)}
						requestType="REIMBURSEMENT"
						companyLevels={companyLevels}
						companyId={companyId}
					/>
				))
			)}

			{/* ── Work time and overtime ── */}
			{([
				{
					title: "Work Time Request Approvals",
					requestType: "WORK_TIME",
					required: workTimeApprovalsRequired,
					approvers: workTimeApprovers,
				},
				{
					title: "Overtime Request Approvals",
					requestType: "OVERTIME",
					required: overtimeApprovalsRequired,
					approvers: overtimeApprovers,
				},
			] as const).map((section) => (
				<div key={section.requestType}>
					<h2 className="font-semibold text-sm py-3">{section.title}</h2>
					{section.required === 0 ? (
						<p className="text-gray-500 text-sm mb-4">
							No approval levels configured. Please set the number of required
							approvals in Company Details.
						</p>
					) : (
						Array.from({ length: section.required }, (_, i) => i + 1).map(
							(level) => (
								<ApproverLevelAccordion
									key={`${section.requestType}-${level}`}
									levelNumber={level}
									approver={section.approvers.find((a) => a.level === level)}
									requestType={section.requestType}
									companyLevels={companyLevels}
									companyId={companyId}
								/>
							),
						)
					)}
				</div>
			))}
		</div>
	);
};

export default CompanyApprover;
